import { useState } from 'react'
import type { CategoryGroup } from '@/features/budget/types'
import { CategoryDropdown } from './CategoryDropdown'

interface Props {
  selectedCount: number
  totalCount: number
  groups: CategoryGroup[]
  onClear: () => void
  onCategorize: (categoryId: string | null) => void
  busy?: boolean
}

/**
 * Sticky toolbar that appears when ≥ 1 transaction is selected. Two
 * actions for the MVP: clear-selection and bulk-categorize. Delete +
 * mark-cleared come with WEB-22.
 */
export function BulkToolbar({
  selectedCount,
  totalCount,
  groups,
  onClear,
  onCategorize,
  busy,
}: Props) {
  const [picking, setPicking] = useState(false)

  return (
    <div
      role="toolbar"
      aria-label="Bulk transaction actions"
      className="sticky top-2 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-blue-50 px-3 py-2 text-sm shadow-sm dark:border-zinc-700 dark:bg-zinc-800"
    >
      <span className="font-medium">
        {selectedCount} selected
        <span className="text-zinc-500"> of {totalCount}</span>
      </span>

      <div className="flex-1" />

      {picking ? (
        <div className="w-72">
          <CategoryDropdown
            groups={groups}
            value={null}
            onChange={(categoryId) => {
              onCategorize(categoryId)
              setPicking(false)
            }}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPicking(true)}
          disabled={busy || selectedCount === 0}
          className="rounded-md bg-blue-600 px-3 py-1 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Categorize…'}
        </button>
      )}

      <button
        type="button"
        onClick={onClear}
        className="rounded-md border border-zinc-300 px-3 py-1 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
      >
        Clear
      </button>
    </div>
  )
}
